// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
//
// The backend can only trigger a Skill Assessment once a candidate's Interview
// Host round is complete (see interview-host API docs). There is currently no
// backend-side "auto-send once the interview finishes" flag, so when a
// recruiter opts into sending both rounds up front (see ConfigureAssessmentModal),
// we remember that choice here and fire the skill-assessment call ourselves the
// next time the candidate list shows the interview as completed.
//
// This is a client-side convenience, not a guarantee: it only fires while a
// recruiter has the candidates page open and refreshes/polls it after the
// candidate finishes their interview.

const STORAGE_KEY = 'skillAssessment.pendingQueue.v1';

export interface PendingSkillAssessmentEntry {
  jobId: string;
  candidateName: string;
  deadlineHours: number;
  queuedAt: string;
}

type PendingQueue = Record<string, PendingSkillAssessmentEntry>;

const readQueue = (): PendingQueue => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PendingQueue) : {};
  } catch (error) {
    console.error('Failed to read pending skill assessment queue:', error);
    return {};
  }
};

const writeQueue = (queue: PendingQueue): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch (error) {
    console.error('Failed to persist pending skill assessment queue:', error);
  }
};

export const queuePendingSkillAssessment = (
  candidateId: string,
  entry: Omit<PendingSkillAssessmentEntry, 'queuedAt'>
): void => {
  const queue = readQueue();
  queue[candidateId] = { ...entry, queuedAt: new Date().toISOString() };
  writeQueue(queue);
};

export const getPendingSkillAssessment = (candidateId: string): PendingSkillAssessmentEntry | null => {
  const queue = readQueue();
  return queue[candidateId] || null;
};

export const clearPendingSkillAssessment = (candidateId: string): void => {
  const queue = readQueue();
  if (queue[candidateId]) {
    delete queue[candidateId];
    writeQueue(queue);
  }
};

export const getAllPendingSkillAssessments = (): PendingQueue => readQueue();
